// Groups (circles + networks) query layer. Circles and Networks share the
// same backend tables and a common adapter shape (`GroupSummary` / `GroupDetail`),
// so one module covers both. The key factory branches on `kind: 'circle' | 'network'`
// where it matters; primitive endpoints (viewer-role, my-pending-requests) share a
// single key.
//
// No 'use client': hooks are React hooks but called from client components.
// Server pages import the keys for prefetch wiring.

import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import type { GroupId, UserId } from '@leanin/shared/ids';
import { getApiClient } from '../api-client';
import { throwApiError } from '../api-error';
import { eventsKeys } from './events';

export type GroupKind = 'circle' | 'network';

// Group kinds that can carry a brand image (logo and/or cover). Spaces are
// topics, not brands, and only support `cover` — the tonal monogram is the
// right mark for a topic. Circles only support `cover` for now.
export type GroupImageKind = 'circle' | 'network' | 'space';
export type GroupImageField = 'logo' | 'cover';
export type GroupImageContentType = 'image/jpeg' | 'image/png' | 'image/webp';

// Which fields each kind supports. Mirrors the server-side authoritative
// table in `apps/server/src/groups/service/logo-upload.ts`
// (`ALLOWED_FIELDS_BY_KIND`). UI consumers call `groupImageSupports(kind,
// field)` to decide whether to render a given upload control at all; the
// server still rejects cross-kind misuse with a 422 if a client tries to
// POST one anyway.
const ALLOWED_IMAGE_FIELDS_BY_KIND: Record<
  GroupImageKind,
  ReadonlyArray<GroupImageField>
> = {
  circle: ['cover'],
  network: ['cover', 'logo'],
  space: ['cover'],
};

/** Whether a given (kind, field) is supported. Use at render time to gate
 *  controls — e.g. don't render the "Upload logo" CTA for spaces. */
export function groupImageSupports(
  kind: GroupImageKind,
  field: GroupImageField,
): boolean {
  return ALLOWED_IMAGE_FIELDS_BY_KIND[kind].includes(field);
}

export type GroupSummary = components['schemas']['GroupSummary'];
export type GroupDetail = components['schemas']['GroupDetail'];
export type GroupMemberView = components['schemas']['GroupMemberView'];
export type NetworkLeaderView = components['schemas']['NetworkLeaderView'];
export type GroupHealthView = components['schemas']['GroupHealthView'];
export type EventSummary = components['schemas']['EventSummary'];
export type ViewerGroupRole = components['schemas']['ViewerGroupRole'];
export type JoinRequestView = components['schemas']['JoinRequestView'];
export type InvitableMemberView = components['schemas']['InvitableMemberView'];

// Mirrors the server's `InviteEmailStatus` discriminated union returned by
// GET /{kind}/{id}/invite-email-status. Modeled locally because the generated
// OpenAPI types haven't caught up yet — the codegen pass on push will regenerate
// them, at which point this can collapse to `components['schemas']['InviteEmailStatus']`.
export type PendingInviteSummary = {
  readonly inviteId: string;
  readonly invitedEmail: string;
  readonly roleOnAccept: 'member' | 'leader';
  readonly createdAt: string;
  readonly expiresAt: string | null;
  // Null for platform/system-issued invites — no human inviter.
  readonly invitedBy: {
    readonly userId: string;
    readonly firstName: string;
    readonly lastName: string;
  } | null;
};

export type InviteEmailStatus =
  | { readonly status: 'invalid_email' }
  | { readonly status: 'off_platform' }
  | {
      readonly status: 'on_platform_invitable';
      readonly member: InvitableMemberView;
    }
  | {
      readonly status: 'already_member';
      readonly member: InvitableMemberView;
      readonly role: 'member' | 'leader';
    }
  | {
      readonly status: 'pending_invite';
      readonly invite: PendingInviteSummary;
    };

// Mirrors the shape returned by `/api/v1/members/me/group-join-requests`
// (also defined in lib/views/groups.ts but kept private to that module).
export type MyPendingJoinRequest = {
  readonly requestId: string;
  readonly requestedAt: string;
  readonly group: GroupSummary;
};

// ---- List args ----

export type ListCirclesArgs = {
  readonly q?: string;
  readonly topic?: string;
  readonly format?: 'in_person' | 'virtual' | 'hybrid';
  readonly language?: string;
  readonly placeText?: string;
  readonly placeCountryCode?: string;
  readonly sort?: 'recent' | 'name';
  readonly pageSize?: number;
};

export type NetworkScope = 'all' | 'mine';

export type ListNetworksArgs = {
  readonly q?: string;
  readonly pageSize?: number;
  readonly region?: string;
  readonly scope?: NetworkScope;
  /** Only honored by the server when `scope === 'mine'`. Surfaces archived
   *  networks the viewer leads, so they appear under the Mine grid. */
  readonly includeArchived?: boolean;
};

// ---- Query keys ----

export const groupKeys = {
  all: ['groups'] as const,

  circle: {
    all: ['groups', 'circle'] as const,
    lists: () => [...groupKeys.circle.all, 'list'] as const,
    list: (args: ListCirclesArgs) =>
      [...groupKeys.circle.lists(), args] as const,
    mine: () => [...groupKeys.circle.all, 'mine'] as const,
    detail: (id: string) => [...groupKeys.circle.all, 'detail', id] as const,
    // Feed posts for a circle's detail page are keyed under
    // `feedKeys.list({ scope: 'group:<id>' })`, not here — so the
    // post-mutation invalidation under `feedKeys.lists()` reaches them.
    events: (id: string, args?: { window?: 'past' | 'upcoming'; pageSize?: number }) =>
      [...eventsKeys.lists(), 'circle', id, { window: args?.window, pageSize: args?.pageSize ?? null }] as const,
    health: (id: string) => [...groupKeys.circle.all, 'health', id] as const,
    conversation: (id: string) =>
      [...groupKeys.circle.all, 'conversation', id] as const,
    joinRequests: (id: string) =>
      [...groupKeys.circle.all, 'join-requests', id] as const,
    inviteNote: (id: string) =>
      [...groupKeys.circle.all, 'invite-note', id] as const,
    invitableMembers: (id: string, q?: string) =>
      [...groupKeys.circle.all, 'invitable', id, { q }] as const,
    inviteEmailStatus: (id: string, email: string) =>
      [
        ...groupKeys.circle.all,
        'invite-email-status',
        id,
        email.toLowerCase(),
      ] as const,
  },

  network: {
    all: ['groups', 'network'] as const,
    lists: () => [...groupKeys.network.all, 'list'] as const,
    list: (args: ListNetworksArgs) =>
      [...groupKeys.network.lists(), args] as const,
    mine: () => [...groupKeys.network.all, 'mine'] as const,
    detail: (id: string) => [...groupKeys.network.all, 'detail', id] as const,
    leaders: (id: string) =>
      [...groupKeys.network.all, 'leaders', id] as const,
    // Feed posts for a network's detail page are keyed under
    // `feedKeys.list({ scope: 'group:<id>' })` — see the circle.feed
    // comment above for rationale.
    circles: (id: string, opts?: { includeArchived?: boolean }) =>
      [
        ...groupKeys.network.all,
        'circles',
        id,
        { includeArchived: opts?.includeArchived ?? false },
      ] as const,
    events: (id: string, args?: { window?: 'past' | 'upcoming'; pageSize?: number }) =>
      [...eventsKeys.lists(), 'network', id, { window: args?.window, pageSize: args?.pageSize ?? null }] as const,
    joinRequests: (id: string) =>
      [...groupKeys.network.all, 'join-requests', id] as const,
    inviteNote: (id: string) =>
      [...groupKeys.network.all, 'invite-note', id] as const,
    invitableMembers: (id: string, q?: string) =>
      [...groupKeys.network.all, 'invitable', id, { q }] as const,
    inviteEmailStatus: (id: string, email: string) =>
      [
        ...groupKeys.network.all,
        'invite-email-status',
        id,
        email.toLowerCase(),
      ] as const,
  },

  // Shared (group-id, kind-agnostic) endpoints.
  // Members list lives at /api/v1/groups/{id}/members and works for circles,
  // networks, and spaces alike — one key, regardless of kind.
  // pageSize is part of the key: the admin fetches 100 and the public view
  // paginates at 24, so they must not share a cache entry.
  members: (id: string, args?: { search?: string; pageSize?: number }) =>
    [...groupKeys.all, 'members', id, { search: args?.search ?? '', pageSize: args?.pageSize ?? null }] as const,
  viewerRole: (groupId: string) =>
    [...groupKeys.all, 'viewer-role', groupId] as const,
  viewerRolesBatch: (groupIds: readonly string[]) =>
    [...groupKeys.all, 'viewer-roles', [...groupIds].sort()] as const,
  myPendingJoinRequests: () =>
    [...groupKeys.all, 'my-pending-join-requests'] as const,
  leaderForRequest: (groupId: string, kind: GroupKind) =>
    [...groupKeys.all, 'leader-for-request', groupId, kind] as const,
};

// ---- Client fetchers: circles ----

export async function fetchCircles(
  args: ListCirclesArgs & { cursor?: string } = {},
) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/circles', {
    params: {
      query: {
        q: args.q,
        topic: args.topic,
        format: args.format,
        language: args.language,
        placeText: args.placeText,
        placeCountryCode: args.placeCountryCode,
        sort: args.sort,
        cursor: args.cursor,
        pageSize: args.pageSize,
      },
    },
  });
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

export async function fetchMyCircles(args?: {
  pageSize?: number;
  includeArchived?: boolean;
}) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/members/me/circles',
    {
      params: {
        query: {
          pageSize: args?.pageSize,
          ...(args?.includeArchived ? { includeArchived: true } : {}),
        },
      },
    },
  );
  if (response.status === 403 || response.status === 404) {
    return { items: [], nextCursor: null, pageSize: 0 } as NonNullable<typeof data>;
  }
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

export type CircleLocationOption = components['schemas']['CircleLocationOption'];

export async function fetchCircleLocations(): Promise<CircleLocationOption[]> {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/circles/locations');
  if (error !== undefined || data === undefined) throwApiError(error);
  return [...data.items];
}

export function useCircleLocations() {
  return useQuery({
    queryKey: [...groupKeys.circle.all, 'locations'] as const,
    queryFn: fetchCircleLocations,
    staleTime: 5 * 60 * 1000,
  });
}

export type CirclePreview = components['schemas']['CirclePreview'];
export type CircleDetailResponse = components['schemas']['GetCircleResponse'];

export async function fetchCircle(id: string): Promise<CircleDetailResponse> {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/api/v1/circles/{id}', {
    params: { path: { id } },
  });
  if (error !== undefined || data === undefined) throwApiError(error, response);
  return data;
}

export async function fetchCircleEvents(args: {
  groupId: string;
  pageSize?: number;
  cursor?: string;
  window?: 'past' | 'upcoming';
}) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/circles/{id}/events',
    {
      params: {
        path: { id: args.groupId },
        query: {
          pageSize: args.pageSize,
          cursor: args.cursor,
          window: args.window,
        },
      },
    },
  );
  if (response.status === 403 || response.status === 404) {
    return { items: [], nextCursor: null, pageSize: 0 } as NonNullable<typeof data>;
  }
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

export async function fetchCircleHealth(
  id: string,
): Promise<GroupHealthView | null> {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/circles/{id}/health',
    { params: { path: { id } } },
  );
  if (response.status === 403 || response.status === 404) return null;
  if (error !== undefined || data === undefined) throwApiError(error);
  return data.data;
}

export async function fetchCircleConversation(id: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/circles/{id}/conversation',
    { params: { path: { id } } },
  );
  if (response.status === 403 || response.status === 404) return null;
  if (error !== undefined || data === undefined) throwApiError(error);
  return data.data;
}

export async function fetchCircleJoinRequests(id: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/groups/{id}/join-requests',
    { params: { path: { id } } },
  );
  if (response.status === 403 || response.status === 404) {
    return { items: [] as JoinRequestView[], nextCursor: null, pageSize: 0 };
  }
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

// Kind-agnostic invite fetchers. The `/circles/{id}/*` and `/networks/{id}/*`
// endpoints expose identical shapes (lifted into shared services in
// `apps/server/.../service/group-invites.ts`); we branch only on the URL.

// Per-(viewer, group) invite-blurb note. Returns the saved string or '' when
// the viewer hasn't saved one yet.
export async function fetchGroupInviteNote(args: {
  kind: GroupKind;
  groupId: string;
}): Promise<string> {
  const client = getApiClient();
  const params = { path: { id: args.groupId } };
  const { data, error } =
    args.kind === 'circle'
      ? await client.GET('/api/v1/circles/{id}/invite-prefs', { params })
      : await client.GET('/api/v1/networks/{id}/invite-prefs', { params });
  if (error !== undefined || data === undefined) throwApiError(error);
  return data.note;
}

export async function fetchInvitableMembers(args: {
  kind: GroupKind;
  groupId: string;
  q?: string;
  email?: string;
  limit?: number;
}): Promise<readonly InvitableMemberView[]> {
  const client = getApiClient();
  const query = {
    ...(args.q !== undefined ? { q: args.q } : {}),
    ...(args.email !== undefined ? { email: args.email } : {}),
    limit: args.limit,
  };
  if (args.kind === 'circle') {
    const { data, error } = await client.GET(
      '/api/v1/circles/{id}/invitable-members',
      { params: { path: { id: args.groupId }, query } },
    );
    if (error !== undefined || data === undefined) throwApiError(error);
    return data.items;
  }
  const { data, error } = await client.GET(
    '/api/v1/networks/{id}/invitable-members',
    { params: { path: { id: args.groupId }, query } },
  );
  if (error !== undefined || data === undefined) throwApiError(error);
  return data.items;
}

// Per-email invite status. Returns a discriminated union that covers every
// terminal state: invalid_email / off_platform / on_platform_invitable /
// already_member / pending_invite. Drives the modal's per-chip hint + send-
// gating, replacing the prior misuse of `invitable-members?email=…` (which
// conflated blocking states with "off-platform" because they were filtered
// upstream).
//
// The generated openapi-types haven't picked up this endpoint yet, so we use
// an untyped fetch + a locally-modeled shape that mirrors the server's Zod
// schema. The codegen pass on push will regenerate the types; this can then
// switch to a typed `client.GET('/api/v1/{kind}s/{id}/invite-email-status')`.
export async function fetchInviteEmailStatus(args: {
  kind: GroupKind;
  groupId: string;
  email: string;
}): Promise<InviteEmailStatus> {
  const path = `/api/v1/${args.kind}s/${args.groupId}/invite-email-status?email=${encodeURIComponent(args.email)}`;
  const client = getApiClient();
  const untypedGet = client.GET as unknown as (
    p: string,
  ) => Promise<{
    data?: InviteEmailStatus;
    error?: unknown;
    response: Response;
  }>;
  const { data, error } = await untypedGet(path);
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

// ---- Client fetchers: networks ----

export async function fetchNetworks(
  args: ListNetworksArgs & { cursor?: string } = {},
) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/networks', {
    params: {
      query: {
        q: args.q,
        cursor: args.cursor,
        pageSize: args.pageSize,
        region: args.region,
        scope: args.scope,
        ...(args.includeArchived ? { includeArchived: true } : {}),
      },
    },
  });
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

export async function fetchMyNetworks(args?: { includeArchived?: boolean }) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/members/me/networks',
    {
      params: {
        query: args?.includeArchived ? { includeArchived: true } : {},
      },
    },
  );
  if (response.status === 403 || response.status === 404) {
    return { items: [], nextCursor: null, pageSize: 0 } as NonNullable<typeof data>;
  }
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

export async function fetchNetwork(id: string): Promise<GroupDetail> {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/api/v1/networks/{id}', {
    params: { path: { id } },
  });
  if (error !== undefined || data === undefined) throwApiError(error, response);
  return data.data;
}

export async function fetchNetworkLeaders(id: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/networks/{id}/leaders',
    { params: { path: { id } } },
  );
  if (response.status === 403 || response.status === 404) {
    return { items: [], nextCursor: null, pageSize: 0 } as NonNullable<typeof data>;
  }
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

export async function fetchNetworkCircles(args: {
  networkId: string;
  pageSize?: number;
  cursor?: string;
  includeArchived?: boolean;
}) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/networks/{id}/circles',
    {
      params: {
        path: { id: args.networkId },
        query: {
          pageSize: args.pageSize,
          cursor: args.cursor,
          ...(args.includeArchived ? { includeArchived: true } : {}),
        },
      },
    },
  );
  if (response.status === 403 || response.status === 404) {
    return { items: [], nextCursor: null, pageSize: 0 } as NonNullable<typeof data>;
  }
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

export async function fetchNetworkEvents(args: {
  networkId: string;
  pageSize?: number;
  cursor?: string;
  window?: 'past' | 'upcoming';
}) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/networks/{id}/events',
    {
      params: {
        path: { id: args.networkId },
        query: {
          pageSize: args.pageSize,
          cursor: args.cursor,
          window: args.window,
        },
      },
    },
  );
  if (response.status === 403 || response.status === 404) {
    return { items: [], nextCursor: null, pageSize: 0 } as NonNullable<typeof data>;
  }
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

export async function fetchNetworkJoinRequests(id: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/groups/{id}/join-requests',
    { params: { path: { id } } },
  );
  if (response.status === 403 || response.status === 404) {
    return { items: [] as JoinRequestView[], nextCursor: null, pageSize: 0 };
  }
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

// ---- Shared (kind-agnostic) ----

// Members listing is now a single endpoint that works across circles, networks
// and spaces. `search` is honored by the server for the latter two; circles
// ignore it. The shape is `GroupMemberView` regardless of kind.
export async function fetchGroupMembers(args: {
  groupId: string;
  pageSize?: number;
  cursor?: string;
  search?: string;
}) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/groups/{id}/members',
    {
      params: {
        path: { id: args.groupId },
        query: {
          pageSize: args.pageSize,
          cursor: args.cursor,
          search: args.search,
        },
      },
    },
  );
  if (response.status === 403 || response.status === 404) {
    return { items: [], nextCursor: null, pageSize: 0 } as NonNullable<typeof data>;
  }
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

export async function fetchViewerGroupRole(
  groupId: string,
): Promise<ViewerGroupRole> {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/groups/{groupId}/viewer-role',
    { params: { path: { groupId } } },
  );
  if (response.status === 403 || response.status === 404) return null;
  if (error !== undefined || data === undefined) throwApiError(error);
  return data.role;
}

export async function fetchViewerGroupRoles(
  groupIds: readonly string[],
): Promise<Map<string, 'leader' | 'member'>> {
  if (groupIds.length === 0) return new Map();
  const client = getApiClient();
  const { data, error } = await client.POST('/api/v1/groups/viewer-roles', {
    body: { groupIds: [...groupIds] },
  });
  if (error !== undefined || data === undefined) throwApiError(error);
  const map = new Map<string, 'leader' | 'member'>();
  for (const [gid, role] of Object.entries(data.roles)) {
    if (role === 'leader' || role === 'member') map.set(gid, role);
  }
  return map;
}

export async function fetchMyPendingJoinRequests(): Promise<
  readonly MyPendingJoinRequest[]
> {
  const client = getApiClient();
  // Endpoint isn't yet in the typed paths; fall through with an untyped cast.
  const untypedGet = client.GET as unknown as (
    path: string,
  ) => Promise<{
    data?: { items: readonly MyPendingJoinRequest[] };
    error?: unknown;
  }>;
  const { data, error } = await untypedGet(
    '/api/v1/members/me/group-join-requests',
  );
  if (error !== undefined || data === undefined) return [];
  return data.items;
}

// ---- Hooks: circles ----

export function useCircles(
  args: ListCirclesArgs,
  options?: Omit<
    UseQueryOptions<
      Awaited<ReturnType<typeof fetchCircles>>,
      Error,
      Awaited<ReturnType<typeof fetchCircles>>
    >,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: groupKeys.circle.list(args),
    queryFn: () => fetchCircles(args),
    ...options,
  });
}

export function useInfiniteCircles(args: ListCirclesArgs = {}) {
  return useInfiniteQuery({
    queryKey: groupKeys.circle.list(args),
    queryFn: ({ pageParam }) =>
      fetchCircles({ ...args, cursor: pageParam ?? undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

export function useMyCircles(pageSize?: number) {
  return useQuery({
    queryKey: groupKeys.circle.mine(),
    queryFn: () => fetchMyCircles({ pageSize, includeArchived: true }),
  });
}

export function useCircle(id: string) {
  return useQuery({
    queryKey: groupKeys.circle.detail(id),
    queryFn: () => fetchCircle(id),
  });
}

export function useCircleEvents(
  groupId: string,
  args?: { pageSize?: number; window?: 'past' | 'upcoming' },
) {
  return useQuery({
    queryKey: groupKeys.circle.events(groupId, { window: args?.window, pageSize: args?.pageSize }),
    queryFn: () =>
      fetchCircleEvents({
        groupId,
        ...(args?.pageSize !== undefined ? { pageSize: args.pageSize } : {}),
        ...(args?.window !== undefined ? { window: args.window } : {}),
      }),
  });
}

export function useCircleHealth(id: string) {
  return useQuery({
    queryKey: groupKeys.circle.health(id),
    queryFn: () => fetchCircleHealth(id),
  });
}

export function useCircleConversation(id: string) {
  return useQuery({
    queryKey: groupKeys.circle.conversation(id),
    queryFn: () => fetchCircleConversation(id),
  });
}

export function useCircleJoinRequests(id: string) {
  return useQuery({
    queryKey: groupKeys.circle.joinRequests(id),
    queryFn: () => fetchCircleJoinRequests(id),
  });
}

// ---- Hooks: networks ----

export function useNetworks(args: ListNetworksArgs) {
  return useQuery({
    queryKey: groupKeys.network.list(args),
    queryFn: () => fetchNetworks(args),
  });
}

export function useInfiniteNetworks(args: ListNetworksArgs = {}) {
  return useInfiniteQuery({
    queryKey: groupKeys.network.list(args),
    queryFn: ({ pageParam }) =>
      fetchNetworks({ ...args, cursor: pageParam ?? undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
}

export function useMyNetworks() {
  return useQuery({
    queryKey: groupKeys.network.mine(),
    queryFn: () => fetchMyNetworks({ includeArchived: true }),
  });
}

export function useNetwork(
  id: string,
  options?: Omit<
    UseQueryOptions<
      Awaited<ReturnType<typeof fetchNetwork>>,
      Error,
      Awaited<ReturnType<typeof fetchNetwork>>
    >,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: groupKeys.network.detail(id),
    queryFn: () => fetchNetwork(id),
    ...options,
  });
}

export function useNetworkLeaders(id: string) {
  return useQuery({
    queryKey: groupKeys.network.leaders(id),
    queryFn: () => fetchNetworkLeaders(id),
  });
}

export function useNetworkCircles(
  networkId: string,
  args?: { pageSize?: number; includeArchived?: boolean },
) {
  return useQuery({
    queryKey: groupKeys.network.circles(networkId, {
      includeArchived: args?.includeArchived ?? false,
    }),
    queryFn: () =>
      fetchNetworkCircles({
        networkId,
        ...(args?.pageSize !== undefined ? { pageSize: args.pageSize } : {}),
        ...(args?.includeArchived ? { includeArchived: true } : {}),
      }),
  });
}

export function useNetworkEvents(
  networkId: string,
  args?: { pageSize?: number; window?: 'past' | 'upcoming' },
) {
  return useQuery({
    queryKey: groupKeys.network.events(networkId, { window: args?.window, pageSize: args?.pageSize }),
    queryFn: () =>
      fetchNetworkEvents({
        networkId,
        ...(args?.pageSize !== undefined ? { pageSize: args.pageSize } : {}),
        ...(args?.window !== undefined ? { window: args.window } : {}),
      }),
  });
}

export function useNetworkJoinRequests(id: string) {
  return useQuery({
    queryKey: groupKeys.network.joinRequests(id),
    queryFn: () => fetchNetworkJoinRequests(id),
  });
}

// ---- Shared hooks ----

export function useGroupMembers(
  groupId: string,
  args?: { search?: string; pageSize?: number },
) {
  return useQuery({
    queryKey: groupKeys.members(groupId, { search: args?.search, pageSize: args?.pageSize }),
    queryFn: () =>
      fetchGroupMembers({
        groupId,
        ...(args?.search !== undefined ? { search: args.search } : {}),
        ...(args?.pageSize !== undefined ? { pageSize: args.pageSize } : {}),
      }),
  });
}

export function useInfiniteGroupMembers(
  groupId: string,
  args?: { search?: string; pageSize?: number; enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: groupKeys.members(groupId, { search: args?.search, pageSize: args?.pageSize }),
    queryFn: ({ pageParam }) =>
      fetchGroupMembers({
        groupId,
        cursor: pageParam ?? undefined,
        ...(args?.search !== undefined ? { search: args.search } : {}),
        ...(args?.pageSize !== undefined ? { pageSize: args.pageSize } : {}),
      }),
    enabled: args?.enabled,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

export function useViewerGroupRole(groupId: string) {
  return useQuery({
    queryKey: groupKeys.viewerRole(groupId),
    queryFn: () => fetchViewerGroupRole(groupId),
  });
}

export function useViewerGroupRoles(groupIds: readonly string[]) {
  return useQuery({
    queryKey: groupKeys.viewerRolesBatch(groupIds),
    queryFn: () => fetchViewerGroupRoles(groupIds),
    enabled: groupIds.length > 0,
  });
}

export function useMyPendingJoinRequests() {
  return useQuery({
    queryKey: groupKeys.myPendingJoinRequests(),
    queryFn: fetchMyPendingJoinRequests,
  });
}

// "Show me the leader of this group" — used by the request-to-join modal to
// surface a face/name on a gated group's preview card. Pulls the first leader
// from the appropriate members list and projects to the small display shape
// the modal uses.
export type GroupLeaderSummary = {
  readonly name: string;
  readonly photoUrl: string | null;
  readonly jobTitle: string | null;
  readonly company: string | null;
};

export async function fetchGroupLeaderForRequest(
  groupId: string,
  kind: GroupKind,
): Promise<GroupLeaderSummary | null> {
  try {
    const result = await fetchGroupMembers({ groupId, pageSize: 50 });
    const items = result.items as readonly GroupMemberView[];
    const leader = items.find((m) => m.role === 'leader');
    if (!leader) return null;
    const fallback = kind === 'circle' ? 'Circle Leader' : 'Network Leader';
    const fullName =
      [leader.firstName, leader.lastName].filter(Boolean).join(' ').trim();
    return {
      name: fullName.length > 0 ? fullName : fallback,
      photoUrl: leader.photoUrl ?? null,
      jobTitle: leader.jobTitle ?? null,
      company: leader.company ?? null,
    };
  } catch {
    return null;
  }
}

export function useGroupLeaderForRequest(
  groupId: string,
  kind: GroupKind,
  enabled = true,
) {
  return useQuery({
    queryKey: groupKeys.leaderForRequest(groupId, kind),
    queryFn: () => fetchGroupLeaderForRequest(groupId, kind),
    enabled: enabled && groupId.length > 0,
    staleTime: 60 * 1000,
  });
}

// Re-export shared types consumers may need; helps avoid scattered
// `@leanin/shared/ids` imports for the common `UserId` cast in callers.
export type { GroupId, UserId };
